import React, { useState, useEffect } from 'react';
import { db } from '@/api/db';
import { Loader2 } from 'lucide-react';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // Octadesk chat na tela de login
  useEffect(() => {
    const scriptId = "octadesk-chat-script";
    if (document.getElementById(scriptId)) return;
    window.octadesk = window.octadesk || {};
    window.octadesk.chatOptions = {
      subDomain: "o210925-3d3",
      showButton: "true",
      openOnMessage: "true",
      showFooterPoweredBy: "true",
      hide: "false",
    };
    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.src = "https://cdn.octadesk.com/embed.js";
    document.body.appendChild(script);
  }, []);

  // Auto-login via token na URL (redirecionamento da plataforma gratuita)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rawToken = params.get('token');
    const freeToken = rawToken?.replace(/^Bearer\s+/i, '') || null;
    const emailParam = params.get('email');
    if (freeToken && emailParam) {
      setLoading(true);
      const API_URL = import.meta.env.VITE_API_URL || '';
      fetch(`${API_URL}/api/auth/token-exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: freeToken, email: emailParam }),
      })
        .then(res => res.json())
        .then(data => {
          if (data.token) {
            localStorage.setItem('portus_token', data.token);
            localStorage.setItem('portus_user', JSON.stringify(data.user));
            window.location.href = '/';
          } else {
            setLoading(false);
            setError(data.error || 'Token inválido ou expirado. Faça login manualmente.');
          }
        })
        .catch(() => {
          setLoading(false);
          setError('Erro ao validar token. Faça login manualmente.');
        });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await db.auth.login(email, password);
      window.location.href = '/';
    } catch (err) {
      setError(translateError(err.message));
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #ffffff 0%, #edfbf4 55%, #f5fef9 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes drift1 {
          0%,100% { transform: translate(0px,    0px)   scale(1);    }
          30%      { transform: translate(160px, -80px)  scale(1.15); }
          65%      { transform: translate(-60px,  120px) scale(0.90); }
        }
        @keyframes drift2 {
          0%,100% { transform: translate(0px,    0px)   scale(1);    }
          35%      { transform: translate(-130px,  60px) scale(1.20); }
          70%      { transform: translate(100px, -100px) scale(0.88); }
        }
        @keyframes drift3 {
          0%,100% { transform: translate(0px,  0px) scale(1);    }
          50%      { transform: translate(80px, 90px) scale(1.10); }
        }
        @keyframes drift4 {
          0%,100% { transform: translate(0px,   0px)  scale(1);    }
          40%      { transform: translate(-90px,-70px) scale(1.08); }
          80%      { transform: translate(50px,  50px) scale(0.95); }
        }
        .login-input {
          width: 100%;
          padding: 12px 14px;
          border: 1.5px solid #E5E9EA;
          border-radius: 12px;
          font-size: 14px;
          color: #2E3233;
          outline: none;
          background: #fff;
          box-sizing: border-box;
          transition: border-color 0.15s, box-shadow 0.15s;
          font-family: inherit;
        }
        .login-input:focus {
          border-color: #00DCA4;
          box-shadow: 0 0 0 3px rgba(0,220,164,0.12);
        }
      `}</style>

      {/* ── Spotlights ── */}
      <div style={{
        position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
        width: 700, height: 700, top: '-20%', left: '-15%',
        background: 'radial-gradient(circle, rgba(0,220,164,0.22) 0%, transparent 65%)',
        filter: 'blur(80px)',
        animation: 'drift1 16s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
        width: 580, height: 580, bottom: '-18%', right: '-8%',
        background: 'radial-gradient(circle, rgba(0,220,164,0.18) 0%, transparent 65%)',
        filter: 'blur(70px)',
        animation: 'drift2 20s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
        width: 380, height: 380, top: '38%', right: '18%',
        background: 'radial-gradient(circle, rgba(176,252,234,0.45) 0%, transparent 65%)',
        filter: 'blur(55px)',
        animation: 'drift3 12s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
        width: 300, height: 300, top: '12%', right: '38%',
        background: 'radial-gradient(circle, rgba(0,220,164,0.12) 0%, transparent 65%)',
        filter: 'blur(45px)',
        animation: 'drift4 9s ease-in-out infinite',
      }} />

      {/* ── Card ── */}
      <div style={{
        position: 'relative', zIndex: 10,
        width: '100%', maxWidth: 420,
        margin: '0 16px',
        background: '#fff',
        borderRadius: 24,
        padding: '44px 40px',
        boxShadow: '0 8px 48px 0 rgba(0,102,76,0.10), 0 1px 0 0 rgba(0,220,164,0.15)',
      }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img src="/logo.png" alt="Portüs Talent Pro" style={{ height: 52, width: 'auto', display: 'block', margin: '0 auto' }} />
        </div>

        <h1 style={{
          fontSize: 22, fontWeight: 600, color: '#003326',
          marginBottom: 4, fontFamily: 'Poppins, sans-serif',
        }}>
          Bem-vindo de volta
        </h1>
        <p style={{ fontSize: 14, color: '#99A6AB', marginBottom: 28 }}>
          Entre com suas credenciais para acessar a plataforma.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#646C6F', marginBottom: 6 }}>
              E-mail
            </label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="seu@email.com"
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#646C6F', marginBottom: 6 }}>
              Senha
            </label>
            <input
              className="login-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
            <a
              href="https://talentos.portusdigital.com.br/forgot-password/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#00DCA4', textDecoration: 'none', marginTop: 8, textAlign: 'right' }}
            >
              Esqueci minha senha
            </a>
          </div>

          {error && (
            <div style={{
              marginBottom: 16, padding: '10px 14px',
              background: '#FFF1F1', borderRadius: 10,
              fontSize: 13, color: '#C0392B',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '14px',
              background: '#00DCA4', color: '#003326',
              borderRadius: 99, fontSize: 15, fontWeight: 700,
              border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'opacity 0.15s',
            }}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 13, color: '#99A6AB', marginTop: 24 }}>
          Ainda não tem conta?{' '}
          <a
            href="https://seja.portustalent.pro/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#00DCA4', fontWeight: 600, textDecoration: 'none' }}
          >
            Fazer cadastro
          </a>
        </p>
      </div>
    </div>
  );
}

function translateError(msg) {
  if (msg.includes('Invalid login credentials')) return 'E-mail ou senha incorretos.';
  if (msg.includes('Email not confirmed'))        return 'Confirme seu e-mail antes de entrar.';
  if (msg.includes('Too many requests'))          return 'Muitas tentativas. Aguarde alguns minutos.';
  return 'Erro ao entrar. Tente novamente.';
}
